/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NotificationHistory, notificationsApi } from "@/api/notifications";
import { CheckBadge } from "@/components/common/CheckBadge";
import { SkeletonRows } from "@/components/common/States";
import { cn } from "@/lib/utils";

const DAY = 86_400_000;

function dayLabel(t: number): string {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d.getTime() === today.getTime()) return "Today";
  if (d.getTime() === today.getTime() - DAY) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** The message as it went out, on one line: no markdown stars, no newlines. */
function oneLine(s: string | undefined): string {
  return (s ?? "").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}

/** Everything the panel sent to Telegram, Discord and the phone, with what failed and why. */
export const SentPanel: React.FC = () => {
  const [onlyFailed, setOnlyFailed] = useState(false);
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["notification-history"],
    queryFn: () => notificationsApi.getHistory(200),
    refetchInterval: 30_000,
  });
  const rows = useMemo(() => data ?? [], [data]);

  const last24 = rows.filter((r) => dataUpdatedAt - new Date(r.created_at).getTime() < DAY);
  const failed24 = last24.filter((r) => !r.success).length;

  const shown = onlyFailed ? rows.filter((r) => !r.success) : rows;
  const groups = useMemo(() => {
    const out: { label: string; items: NotificationHistory[] }[] = [];
    for (const r of shown) {
      const label = dayLabel(new Date(r.created_at).getTime());
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(r);
      else out.push({ label, items: [r] });
    }
    return out;
  }, [shown]);

  if (isLoading) return <SkeletonRows rows={5} />;

  return (
    <div className="nc-sent">
      <div className="nc-sent-bar">
        <span className="nc-sent-sum">
          {`${last24.length} sent in 24 h`}
          {failed24 > 0 && <b className="nc-bad">{` · ${failed24} failed`}</b>}
        </span>
        <div className="sw-seg" role="group" aria-label="Filter">
          <button type="button" aria-pressed={!onlyFailed} onClick={() => setOnlyFailed(false)}>
            All
          </button>
          <button type="button" aria-pressed={onlyFailed} onClick={() => setOnlyFailed(true)}>
            Failed
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="sw-empty">{onlyFailed ? "Nothing failed." : "Nothing has been sent yet."}</div>
      ) : (
        groups.map((g) => (
          <section key={g.label}>
            <h3 className="nc-day">{`${g.label} · ${g.items.length}`}</h3>
            <ul className="nc-sent-list">
              {g.items.map((r) => (
                <li key={r.id} className={cn("nc-sent-row", !r.success && "nc-sent-row--bad")}>
                  <span className="nc-sent-ico">
                    {r.success ? <CheckBadge size={16} className="text-[var(--gh-ok)]" /> : <i className="nc-x" aria-label="failed" />}
                  </span>
                  <div className="nc-sent-main">
                    <div className="nc-sent-top">
                      <b>{r.event_name || "Notification"}</b>
                      <span>{r.channel_name || `channel ${r.channel_id}`}</span>
                      <time>{new Date(r.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}</time>
                    </div>
                    {r.payload && <p>{oneLine(r.payload)}</p>}
                    {!r.success && r.error_message && <p className="nc-bad">{oneLine(r.error_message)}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
};
